import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { format } from "date-fns";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Heading } from "@/components/heading";
import { PostForm, type PostFormSchemaValues } from "@/forms/post-form";
import type { postsEditarQuery } from "./__generated__/postsEditarQuery.graphql";
import type { postsEditarUpdateMutation } from "./__generated__/postsEditarUpdateMutation.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(general)/moderacao/posts/$slug/editar",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { slug: postSlug } = useParams({ from: Route.id });
	const { club } = useRouteContext({ from: Route.id });

	const data = useLazyLoadQuery<postsEditarQuery>(
		graphql`
			query postsEditarQuery($club: String!, $postSlug: String!) {
				club(slug: $club) {
					id
					groups {
						spaces {
							id
							name
						}
					}
				}
				post(slug: $postSlug, club: $club) {
					id
					title
					content
					slug
					pinned
					pinnedUniversal
					hideMetadata
					commentPolicy
					scheduledPublishDate
					space {
						id
					}
					coverContent {
						__typename
						... on ImageFile {
							id
							signedUrl
						}
						... on VideoFile {
							id
							signedUrl
							muxId
						}
					}
					attachments {
						id
						name
						signedUrl
						size
					}
				}
			}
		`,
		{ club: club.slug, postSlug },
	);

	const spaces = data.club.groups.filter(Boolean).flatMap((g) => g.spaces);
	const post = data.post;

	const [updatePost] = useMutation<postsEditarUpdateMutation>(graphql`
		mutation postsEditarUpdateMutation(
			$slug: String!
			$input: UpdatePostInput!
			$club: String!
		) {
			updatePost(input: $input, postSlug: $slug, club: $club) {
				id
				title
				content
				slug
				url
				pinned
				pinnedUniversal
				publishDate
				commentCount
				commentPolicy
				hideMetadata
				votes
				userVote {
					vote
				}
				author {
					displayName
					username
					avatarFallback
					photo {
						signedUrl
					}
				}
				space {
					name
				}
				coverContent {
					__typename
				}
				attachments {
					id
					name
					signedUrl
					size
				}
				scheduledPublishDate
			}
		}
	`);

	const isScheduled = Boolean(post.scheduledPublishDate);

	const coverValue = (() => {
		const cc = post.coverContent;
		if (!cc) return null;
		if (cc.__typename === "ImageFile") {
			return { id: cc.id, url: cc.signedUrl };
		}
		if (cc.__typename === "VideoFile") {
			return {
				id: cc.id,
				url: cc.signedUrl,
				muxId: cc.muxId ?? undefined,
			};
		}
		return null;
	})();

	const onSubmit = (value: PostFormSchemaValues) => {
		return updatePost({
			variables: {
				slug: postSlug,
				club: club.slug,
				input: {
					title: value.title,
					content: JSON.stringify(value.content),
					space: value.space,
					cover: value.cover
						? { coverContent: value.cover.muxId ?? value.cover.id }
						: undefined,
					attachments: value.attachments.map((a) => ({ file: a.id })),
					commentPolicy: value.commentPolicy.toLowerCase(),
					pinned: value.pinned,
					pinnedUniversal: value.pinnedUniversal,
					hideMetadata: value.hideMetadata,
					notifyMembers: value.notifyUsers,
					scheduledPublishDate:
						value.schedule && value.scheduledDate
							? new Date(value.scheduledDate).toISOString()
							: undefined,
				},
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Post atualizado com sucesso!");

				navigate({ to: "/c/$club/moderacao" });
			},
			onError: () => {
				toast.error(
					"Não foi possível atualizar o post no momento. Verifique os dados preenchidos e tente novamente.",
				);
			},
			updater: (store) => {
				const clubRecord = store.get(data.club.id);
				if (clubRecord) {
					clubRecord.invalidateRecord();
				}
			},
		});
	};

	return (
		<div className="size-full flex items-center justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6 border rounded-md">
				<Heading>Editar Post</Heading>

				<PostForm
					onSubmit={onSubmit}
					spaces={spaces}
					defaultValues={{
						title: post.title,
						content: post.content as PostFormSchemaValues["content"],
						space: post.space?.id ?? spaces[0]?.id ?? "",
						cover: coverValue,
						attachments: (post.attachments ?? []).map((a) => ({
							id: a.id,
							url: a.signedUrl,
							name: a.name,
							size: a.size,
						})),
						schedule: isScheduled,
						scheduledDate: post.scheduledPublishDate
							? format(
									new Date(post.scheduledPublishDate as string),
									`yyyy-MM-dd"T"HH:mm`,
								).slice(0, 16)
							: null,
						commentPolicy:
							post.commentPolicy === "RESTRICTED" ? "RESTRICTED" : "ALLOWED",
						pinnedUniversal: post.pinnedUniversal,
						pinned: post.pinned,
						hideMetadata: post.hideMetadata,
						notifyUsers: false,
					}}
				/>
			</div>
		</div>
	);
}
